import { create } from 'zustand';

import type { CardId, ConnectionId, ShapeId } from './types';

export type BoardElementId = CardId | ConnectionId | ShapeId;
export type BoardElementType = 'card' | 'cluster' | 'connection' | 'shape' | 'line' | 'text';

interface CurrentBoardElement {
  id: BoardElementId;
  type: BoardElementType;
}

interface BoardSelectionState {
  connectionPointer: { x: number; y: number } | null;
  connectingFromId: BoardElementId | null;
  currentElement: CurrentBoardElement | null;
  isSelecting: boolean;
  selectedIds: BoardElementId[];
  selectionArea: DOMRectInit | null;
  selectionAreaViewport: DOMRectInit | null;
  selectionStart: { x: number; y: number } | null;
}

interface BoardSelectionActions {
  addSelectedId: (selectedId: BoardElementId) => void;
  clearSelection: () => void;
  removeSelectedId: (selectedId: BoardElementId) => void;
  setConnectionPointer: (connectionPointer: { x: number; y: number } | null) => void;
  setConnectingFromId: (connectingFromId: BoardElementId | null) => void;
  setCurrentElement: (currentElement: CurrentBoardElement | null) => void;
  setIsSelecting: (isSelecting: boolean) => void;
  setSelectedIds: (selectedIds: BoardElementId[]) => void;
  setSelectionArea: (selectionArea: DOMRectInit | null) => void;
  setSelectionAreaViewport: (selectionAreaViewport: DOMRectInit | null) => void;
  setSelectionStart: (selectionStart: { x: number; y: number } | null) => void;
  toggleSelectedId: (selectedId: BoardElementId) => void;
  resetBoardSelection: () => void;
}

export type BoardSelectionStore = BoardSelectionState & BoardSelectionActions;

const initialState: BoardSelectionState = {
  connectionPointer: null,
  connectingFromId: null,
  currentElement: null,
  isSelecting: false,
  selectedIds: [],
  selectionArea: null,
  selectionAreaViewport: null,
  selectionStart: null,
};

export const useBoardSelectionStore = create<BoardSelectionStore>((set) => ({
  ...initialState,
  addSelectedId: (selectedId) =>
    set((state) => ({
      selectedIds: state.selectedIds.includes(selectedId)
        ? state.selectedIds
        : [...state.selectedIds, selectedId],
    })),
  clearSelection: () =>
    set({
      currentElement: null,
      connectionPointer: null,
      isSelecting: false,
      selectedIds: [],
      selectionArea: null,
      selectionAreaViewport: null,
      selectionStart: null,
    }),
  removeSelectedId: (selectedId) =>
    set((state) => ({
      selectedIds: state.selectedIds.filter(
        (currentSelectedId) => currentSelectedId !== selectedId
      ),
    })),
  setConnectionPointer: (connectionPointer) => set({ connectionPointer }),
  setConnectingFromId: (connectingFromId) =>
    set({
      connectingFromId,
      connectionPointer: null,
    }),
  setCurrentElement: (currentElement) => set({ currentElement }),
  setIsSelecting: (isSelecting) => set({ isSelecting }),
  setSelectedIds: (selectedIds) => set({ selectedIds }),
  setSelectionArea: (selectionArea) => set({ selectionArea }),
  setSelectionAreaViewport: (selectionAreaViewport) => set({ selectionAreaViewport }),
  setSelectionStart: (selectionStart) => set({ selectionStart }),
  toggleSelectedId: (selectedId) =>
    set((state) => ({
      selectedIds: state.selectedIds.includes(selectedId)
        ? state.selectedIds.filter((currentSelectedId) => currentSelectedId !== selectedId)
        : [...state.selectedIds, selectedId],
    })),
  resetBoardSelection: () => set(initialState),
}));
